import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { ReactComponent as ComputerIcon } from '~/assets/images/computer-icon.svg';
import { ReactComponent as OpenTerminalIcon } from '~/assets/images/open-terminal.svg';
import { ReactComponent as PlayIcon } from '~/assets/images/play-icon.svg';
import { ReactComponent as PowerIcon } from '~/assets/images/power-icon.svg';
import { ReactComponent as RefreshIcon } from '~/assets/images/refresh-icon.svg';
import LoadingButtonWrapper from '~/components/LoadingButtonWrapper';
import SSOLink from '~/components/SSOLink';

import OpenTerminalSafariTooltip from './OpenTerminalSafariTooltip';

type ServerActionButtonsProps = {
  serverAddonStatus?: boolean;
  disableStartServer?: boolean;
  disableRebootServer?: boolean;
  disableStopServer?: boolean;
  onStartClick: () => void;
  onRebootClick: () => void;
  onStopClick?: () => void;
  systemConsoleData: any;
  hostingId?: string;
  buttonGap?: number;
  serverType?: string;
  serverStatusLoading?: boolean;
};

const ServerActionButtons: React.FC<ServerActionButtonsProps> = ({
  serverAddonStatus,
  disableStartServer,
  disableRebootServer,
  disableStopServer,
  onStartClick,
  onRebootClick,
  onStopClick,
  systemConsoleData,
  hostingId,
  buttonGap = 4,
  serverType = '',
  serverStatusLoading = false,
}) => {
  const { t } = useTranslation('hosting', { keyPrefix: 'hostingNova' });
  const id = hostingId || '';
  const isVpsCpanel = serverType === 'vps_cpanel';
  const isSelfManagedVps = serverType === 'vps';

  const ssoLinkProps = {
    ariaLabel: isSelfManagedVps ? 'BrowserTerminal' : 'SystemConsole',
    queryParams: {
      ...systemConsoleData?.queryParams,
      ...(isSelfManagedVps && { app: 'ssh' }),
    },
    startIcon: isSelfManagedVps ? <OpenTerminalIcon /> : <ComputerIcon />,
  };

  const consoleButtonLabel = isSelfManagedVps
    ? t('serverActionButtons.terminalBtn')
    : isVpsCpanel
    ? t('serverActionButtons.recoveryConsoleBtn')
    : t('serverActionButtons.manageConsole');

  const isVpsWindowsPlesk = serverType === 'vps_windows_plesk';
  const showConsoleButton = !!systemConsoleData?.canShow;
  const buttonColumns = isVpsWindowsPlesk
    ? { xs: 12, sm: 4, md: 4 }
    : { xs: 12, sm: 6, md: 3 };

  const consoleActionButton = () => {
    // SSOLink has no loading state, so stand in for it until `canShow` resolves.
    if (serverStatusLoading) {
      return (
        <LoadingButtonWrapper
          fullWidth
          variant="outlined"
          aria-label={ssoLinkProps.ariaLabel}
          loading
          startIcon={ssoLinkProps.startIcon}
          size="large"
        >
          <Typography variant="h3">{consoleButtonLabel}</Typography>
        </LoadingButtonWrapper>
      );
    }

    if (isVpsWindowsPlesk && !showConsoleButton) {
      return null;
    }
    return (
      <SSOLink
        aria-label={ssoLinkProps.ariaLabel}
        type={systemConsoleData?.type}
        queryParams={ssoLinkProps.queryParams}
        fluid
        showIcon
        variant="outlined"
        hostingId={id}
        size="large"
        disabled={!showConsoleButton}
        startIcon={ssoLinkProps.startIcon}
      >
        <Typography variant="h3">{consoleButtonLabel}</Typography>
      </SSOLink>
    );
  };

  return (
    <Grid container spacing={buttonGap}>
      <Grid item {...buttonColumns}>
        <LoadingButtonWrapper
          fullWidth
          variant="outlined"
          aria-label="StartServer"
          disabled={disableStartServer || serverAddonStatus}
          onClick={onStartClick}
          loading={serverStatusLoading}
          startIcon={<PlayIcon />}
          size="large"
        >
          <Typography variant="h3">
            {t('serverActionButtons.startServerBtn')}
          </Typography>
        </LoadingButtonWrapper>
      </Grid>
      <Grid item {...buttonColumns}>
        <LoadingButtonWrapper
          fullWidth
          variant="outlined"
          aria-label="RebootServer"
          disabled={disableRebootServer || serverAddonStatus}
          loading={serverStatusLoading}
          onClick={onRebootClick}
          startIcon={<RefreshIcon />}
          size="large"
        >
          <Typography variant="h3">
            {t('serverActionButtons.rebootServerBtn')}
          </Typography>
        </LoadingButtonWrapper>
      </Grid>
      <Grid item {...buttonColumns}>
        <LoadingButtonWrapper
          fullWidth
          variant="outlined"
          aria-label="StopServer"
          disabled={disableStopServer || serverAddonStatus}
          loading={serverStatusLoading}
          onClick={onStopClick}
          startIcon={<PowerIcon />}
          size="large"
        >
          <Typography variant="h3">
            {t('serverActionButtons.stopServerBtn')}
          </Typography>
        </LoadingButtonWrapper>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        {isSelfManagedVps ? (
          <OpenTerminalSafariTooltip>
            {consoleActionButton()}
          </OpenTerminalSafariTooltip>
        ) : (
          consoleActionButton()
        )}
      </Grid>
    </Grid>
  );
};

export default ServerActionButtons;
